import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { Typography, List, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Box from '../Box'
import CostDiagram from '../CostDiagram'
import GroupCostLine from './GroupCostLine'
import { CalculatorContext } from '../Context/CalculatorContext'
import { costSourceDisplayValues } from '../../strings/costSourceDisplayValues'

const useStyles = makeStyles((theme) => ({
    root: {},
    result: {},
    subList: {
        marginLeft: theme.spacing(1)
    },
    blockSource: {
        borderColor: theme.palette.text.primary,
        borderLeft: 'solid 1px',
        paddingLeft: theme.spacing(1)
    },
    childrenDivider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    }
}))

const generateLabel = (key, index = 0) => {
    if (key.startsWith('transaction')) {
        return `Transactions - Block ${index + 1}`
    } else if (
        !key.startsWith('Storage') &&
        !key.startsWith('Master Delivery') &&
        !key.startsWith('Migration') &&
        !key.startsWith('Restore')
    ) {
        return `Transactions - ${key}`
    } else {
        return key
    }
}

const GroupCostBox = ({
    title,
    cost: { total, sources, blocks },
    showDetails = false,
    showSummary = true,
    children
}) => {
    const classes = useStyles()
    const { currency } = useContext(CalculatorContext)

    const mapSources = (isDetails = false) => {
        const elements = Object.entries(sources).map(([key, value]) =>
            value > 0 ? listElement(key, value, isDetails) : null
        )
        return elements.some((el) => el !== null) ? (
            <List>{elements}</List>
        ) : null
    }

    const mapBlocks = () => {
        return Object.entries(blocks).map(
            ([key, { total, sources }], index) => {
                return total > 0 ? (
                    <List key={key}>
                        <li key={'title'}>
                            <GroupCostLine
                                label={generateLabel(key, index)}
                                total={total}
                            />
                        </li>
                        <li key={'sublist'}>
                            <List className={classes.subList}>
                                {mapBlockSources(sources, key)}
                            </List>
                        </li>
                    </List>
                ) : null
            }
        )
    }

    const combineBlockSources = () =>
        blocks
            ? Object.entries(blocks).reduce((acc, [key, { sources }]) => {
                  Object.entries(sources).forEach(([key, value]) => {
                      acc[key] ? (acc[key] += value) : (acc[key] = value)
                  })
                  return acc
              }, {})
            : {}

    const mapBlockSources = (sources) => {
        return Object.entries(sources).map(([key, value]) => {
            return value > 0
                ? listElement(key, value, true, classes.blockSource)
                : null
        })
    }

    const listElement = (key, value, isDetails, classes = '') => (
        <li key={key} className={classes}>
            <GroupCostLine
                currency={currency}
                label={
                    isDetails
                        ? costSourceDisplayValues[key].label
                        : generateLabel(key)
                }
                total={value}
            />
        </li>
    )

    return (
        <Box>
            <div className={classes.root}>
                {title ? (
                    <Typography
                        variant={'h3'}
                        color={'textPrimary'}
                        gutterBottom
                    >
                        {title}
                    </Typography>
                ) : null}
                <div className={classes.result}>
                    {blocks ? mapBlocks() : null}

                    {sources ? mapSources(!!showDetails) : null}

                    {showSummary ? (
                        <>
                            <Divider />
                            <GroupCostLine
                                label="Monthly estimated cost"
                                total={total}
                            />
                            <GroupCostLine
                                label="Yearly estimated cost"
                                total={total * 12}
                            />
                        </>
                    ) : null}

                    {children ? (
                        <>
                            <Divider className={classes.childrenDivider} />
                            {children}
                        </>
                    ) : null}
                </div>
            </div>
            {showDetails ? (
                <>
                    <Divider />
                    <CostDiagram sources={sources || combineBlockSources()} />
                </>
            ) : null}
        </Box>
    )
}

GroupCostBox.propTypes = {
    title: PropTypes.string,
    cost: PropTypes.object.isRequired,
    showDetails: PropTypes.bool,
    showSummary: PropTypes.bool
}

export default GroupCostBox
